import { ApplicationNote, NoteEntityPayload } from "./types";
import { useEffect, useRef } from "react";
import { observe } from "react-intersection-observer";

type TipTapJsonNode = {
  type?: string;
  attrs?: {
    entity_name?: string;
    entity_identifier?: string | number;
  };
  content?: TipTapJsonNode[];
};

export const extractEntitiesFromJson = (
  json: { content?: TipTapJsonNode[] } | null | undefined
): NoteEntityPayload["entities"] => {
  const entities: NoteEntityPayload["entities"] = [];

  const traverseContent = (content: TipTapJsonNode[]) => {
    if (!Array.isArray(content)) return;

    content.forEach((item) => {
      if (item.type === "mention" && item.attrs) {
        const subjectType = item.attrs.entity_name;
        const rawId = item.attrs.entity_identifier;
        if (subjectType != null && rawId != null) {
          const subjectId = typeof rawId === "number" ? rawId : Number(rawId);
          if (!Number.isNaN(subjectId))
            entities.push({
              kind: "mention",
              subject_type: subjectType,
              subject_id: subjectId
            });
        }
      }

      if (item.content) traverseContent(item.content);
    });
  };

  if (json?.content) traverseContent(json.content);

  return entities;
};

const EMPTY_P_TAG_REGEX = /^(?:<p[^>]*>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>\s*)+$/;

export const isTextEmpty = (text: string | undefined): boolean => {
  if (!text) return true;
  if (EMPTY_P_TAG_REGEX.test(text.trim())) return true;
  return text.trim() === "";
};

export const isUnread = (note: ApplicationNote, currentUserId: number) => {
  if (currentUserId === note.author?.id) return false;

  const mentionedNotification = note.rich_text?.notifications?.find(
    (ki) =>
      ki.kind === "mention" &&
      ki.user != null &&
      String(ki.user.id) === String(currentUserId)
  );

  if (mentionedNotification?.read_at !== null) return false;

  return (
    mentionedNotification.user != null &&
    String(mentionedNotification.user.id) !== String(note.author?.id)
  );
};

export const getMentionNotificationId = (
  note: ApplicationNote,
  currentUserId: number
) => {
  const mentionedNotification = note.rich_text?.notifications?.find(
    (ki) =>
      ki.kind === "mention" &&
      ki.user != null &&
      String(ki.user.id) === String(currentUserId)
  );

  if (!mentionedNotification) return undefined;

  return mentionedNotification.id || undefined;
};

export const useMarkRead = (
  items: { id: string; read: boolean }[],
  getElById: (id: string) => HTMLElement | null,
  markReadBatch: (ids: string[]) => Promise<void>,
  opts = { threshold: 0.5, dwellMs: 0, flushMs: 300 }
) => {
  const seen = useRef<Set<string>>(new Set());
  const queue = useRef<Set<string>>(new Set());
  const timers = useRef<Map<string, number>>(new Map());
  const flushTimer = useRef<number | null>(null);

  const scheduleFlush = () => {
    if (flushTimer.current) clearTimeout(flushTimer.current);
    flushTimer.current = window.setTimeout(async () => {
      const ids = [...queue.current];
      if (!ids.length) return;
      queue.current.clear();
      await markReadBatch(ids);
    }, opts.flushMs) as unknown as number;
  };

  useEffect(() => {
    const unobservers: (() => void)[] = [];

    for (const it of items) {
      if (it.read) {
        seen.current.add(it.id);
        continue;
      }
      const el = getElById(it.id);
      if (!el) continue;

      const id = it.id;
      const unobserve = observe(
        el,
        (inView, entry) => {
          if (seen.current.has(id)) return;

          if (inView && entry.intersectionRatio >= opts.threshold) {
            if (opts.dwellMs && !timers.current.has(id)) {
              const t = window.setTimeout(() => {
                timers.current.delete(id);
                seen.current.add(id);
                queue.current.add(id);
                scheduleFlush();
                unobserve();
              }, opts.dwellMs) as unknown as number;
              timers.current.set(id, t);
            } else if (!opts.dwellMs) {
              seen.current.add(id);
              queue.current.add(id);
              scheduleFlush();
              unobserve();
            }
          } else {
            const t = timers.current.get(id);
            if (t) {
              clearTimeout(t);
              timers.current.delete(id);
            }
          }
        },
        { threshold: opts.threshold }
      );
      unobservers.push(unobserve);
    }

    return () => {
      unobservers.forEach((unobserve) => unobserve());
      timers.current.forEach((t) => clearTimeout(t));
      timers.current.clear();
      if (flushTimer.current) clearTimeout(flushTimer.current);
    };
  }, [items, opts.threshold, opts.dwellMs, opts.flushMs]);
};
